import { BehaviorSubject, combineLatest, fromEvent, iif, Observable, of } from 'rxjs';
import { CardData } from '../../../../models/print-cards/card-data';
import { debounceTime, distinctUntilChanged, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { SectionRowViewModelUtils } from '../../../../utils/section-row-view-model-utils';
import { PrintCardMenu } from '../../../../models/menu/print-card/print-card-menu';
import { CompanyConfiguration } from '../../../../models/company/dto/company-configuration';
import { LocationConfiguration } from '../../../../models/company/dto/location-configuration';
import { Injectable } from '@angular/core';
import { InjectorService } from '../../../services/injector.service';
import { BaseViewModel } from '../../../../models/base/base-view-model';
import { Menu } from '../../../../models/menu/menu';
import { exists } from '../../../../functions/exists';
import { VariantAssetService } from '../../../services/variant-asset-service';
import { DistinctUtils } from '../../../../utils/distinct.utils';
import { LabelDomainModel } from '../../../../domain/label-domain-model';
import { Label } from '../../../../models/menu/labels/label';
import { LocationDomainModel } from '../../../../domain/location-domain-model';
import { CompanyDomainModel } from '../../../../domain/company-domain-model';
import { ShelfTalkerMenu } from '../../../../models/menu/shelf-talkers/shelf-talker-menu';
import { ShelfTalkerCardData } from '../../../../models/shelf-talkers/shelf-talker-card-data';
import { ShelfTalkerUtils } from '../../../../utils/shelf-talker-utils';

@Injectable()
export class RenderPrintCardLiveViewViewModel extends BaseViewModel {
  constructor(
    private companyDomainModel: CompanyDomainModel,
    private labelDomainModel: LabelDomainModel,
    private locationDomainModel: LocationDomainModel,
    private variantAssetService: VariantAssetService,
    private injectorService: InjectorService
  ) {
    super();
    // fetch all variant assets for the give cards, so that as they move around between cards the assets will have
    // been pre-fetched and ready to be displayed
    this.variantAssetService.connectToOverrideVariantIds([]);
  }

  connectToLabels = (labelsData: any | null) => {
    const labels = this.injectorService?.Deserialize?.arrayOf(Label, labelsData);
    this.labelDomainModel.labelDataSentFromDashboardApp(labels);
  };

  public readonly companyConfig$ = this.companyDomainModel.companyConfig$;
  connectToCompanyConfig = (companyConfigData: any | null) => {
    const companyConfig = this.injectorService?.Deserialize?.instanceOf(CompanyConfiguration, companyConfigData);
    this.companyDomainModel.companyConfigSentFromDashboardApp(companyConfig);
  };

  public readonly locationConfig$ = this.locationDomainModel.locationConfig$;
  connectToLocationConfig = (locationConfigData: any | null) => {
    const locationConfig = this.injectorService?.Deserialize?.instanceOf(LocationConfiguration, locationConfigData);
    this.locationDomainModel.locationConfigSentFromDashboardApp(locationConfig);
  };

  private readonly _metadata = new BehaviorSubject<any | null>(null);
  public readonly metadata$ = this._metadata as Observable<any | null>;
  connectToMetadata = (metadata: any | null) => this._metadata.next(metadata);

  public readonly loadingVariantAssets$ = this.variantAssetService.variantMetadataLoaded$.pipe(
    map(loaded => !loaded),
    startWith(true),
    distinctUntilChanged()
  );

  public readonly loadingVariantAssetsDashboardSignal$ = this.loadingVariantAssets$.pipe(
    debounceTime(500),
    startWith(true),
    distinctUntilChanged()
  );

  private readonly _printCardMenu = new BehaviorSubject<PrintCardMenu | null>(null);
  public readonly printCardMenu$ = combineLatest([this._printCardMenu, this.companyConfig$, this.locationConfig$]).pipe(
    map(([menu, companyConfig, locationConfig]) => {
      menu?.setImplicitProperties(companyConfig, locationConfig);
      return menu;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  connectToPrintCardMenu = (printCardMenuData: any | null) => {
    const menu = this.injectorService?.Deserialize?.instanceOf(Menu, printCardMenuData);
    const printCardMenu = menu instanceof PrintCardMenu ? menu : null;
    this._printCardMenu.next(printCardMenu);
  };

  public readonly layoutInstructions$ = this.printCardMenu$.pipe(map(menu => menu?.getLayoutInstructions()));

  private readonly _variantIds = new BehaviorSubject<string[] | null>(null);
  public readonly variantIds$ = this._variantIds as Observable<string[] | null>;
  connectToVariantIds = (variantIds: string[] | null) => this._variantIds.next(variantIds);

  private readonly _shelfTalkerMenu = new BehaviorSubject<ShelfTalkerMenu | null>(null);
  public readonly shelfTalkerMenu$ = this._shelfTalkerMenu as Observable<ShelfTalkerMenu | null>;
  connectToShelfTalkerMenu = (shelfTalkerMenuData: any | null) => {
    const menu = this.injectorService?.Deserialize?.instanceOf(Menu, shelfTalkerMenuData);
    const shelfTalkerMenu = menu instanceof ShelfTalkerMenu ? menu : null;
    this._shelfTalkerMenu.next(shelfTalkerMenu);
  };

  public readonly cardMenu$ = combineLatest([this.printCardMenu$, this.shelfTalkerMenu$]).pipe(
    map(([printCardMenu, shelfTalkerMenu]) => printCardMenu || shelfTalkerMenu),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly findCardsThatLayoutOntoPaperForMultiSizedStack$ = combineLatest([
    this.layoutInstructions$,
    this.variantIds$
  ]).pipe(
    map(([layoutInstructions, variantIds]) => {
      return (
        layoutInstructions?.find(instructions => {
          return instructions?.enabledVariantIds?.intersection(variantIds)?.length > 0;
        }) ?? null
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Note that multiple card stack sizes for a single menu is only plugged into PRINT CARD MENUS,
   * and NOT plugged into SHELF TALKER MENUS.
   */
  public readonly cardsThatLayoutOntoPaper$ = this.cardMenu$.pipe(
    switchMap(menu => {
      return iif(
        () => menu instanceof PrintCardMenu && menu?.sections?.length > 1,
        this.findCardsThatLayoutOntoPaperForMultiSizedStack$,
        of(menu?.sections?.firstOrNull())
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly adobeFontFamilyCssUrl$ = this.cardMenu$.pipe(
    map(menu => menu?.getAdobeFontFamilyCssUrl() ?? null),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly cardHeightAndWidth$ = this.cardsThatLayoutOntoPaper$.pipe(
    map(cardsThatLayoutOntoPaper => {
      return {
        cardHeight: cardsThatLayoutOntoPaper?.cardPreviewHeightInPx() ?? undefined,
        cardWidth: cardsThatLayoutOntoPaper?.cardPreviewWidthInPx() ?? undefined
      };
    }),
    distinctUntilChanged((a, b) => a.cardHeight === b.cardHeight && a.cardWidth === b.cardWidth)
  );

  private readonly _sectionId = new BehaviorSubject<string | null>(null);
  public readonly sectionId$ = this._sectionId as Observable<string | null>;
  connectToSectionId = (sectionId: string | null) => this._sectionId.next(sectionId);

  protected readonly printCardGrouping$: Observable<CardData | null> = combineLatest([
    this.printCardMenu$,
    this.cardsThatLayoutOntoPaper$,
    this.variantIds$,
    this.companyConfig$,
    this.locationConfig$,
    this.metadata$
  ]).pipe(
    map(([menu, cardStack, variantIds, companyConfig, locConfig, metadata]) => {
      const generateCardData = SectionRowViewModelUtils.generateRowViewModels;
      const products = cardStack?.products;
      return generateCardData(cardStack, menu, companyConfig, locConfig, products, [], variantIds, metadata)
        ?.filter((card): card is CardData => card instanceof CardData)
        ?.filter(cardData => cardData?.rowVariants?.map(v => v?.id)?.intersection(variantIds)?.length > 0)
        ?.firstOrNull();
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  protected shelfTalkerGrouping$: Observable<ShelfTalkerCardData | null> = combineLatest([
    this.shelfTalkerMenu$,
    this.sectionId$,
    this.companyConfig$,
    this.locationConfig$
  ]).pipe(
    map(([menu, sectionId, companyConfig, locConfig]) => {
      const cardId = sectionId || menu?.sections?.firstOrNull()?.id;
      const generateData = ShelfTalkerUtils.generateShelfTalkerCardData;
      return generateData(menu, companyConfig, locConfig, [cardId])
        ?.filter((data): data is ShelfTalkerCardData => data instanceof ShelfTalkerCardData)
        ?.firstOrNull();
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly cardGrouping$: Observable<CardData | ShelfTalkerCardData | null> = this.shelfTalkerMenu$.pipe(
    switchMap(shelfTalkerMenu => {
      return iif(() => exists(shelfTalkerMenu), this.shelfTalkerGrouping$, this.printCardGrouping$);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly showWhiteForegroundForShelfTalkerLoading$ = this.cardMenu$.pipe(
    switchMap(menu => {
      return iif(
        () => !menu,
        of(true),
        combineLatest([this.cardMenu$, this.loadingVariantAssets$]).pipe(
          map(([shelfTalkerMenu, loading]) => shelfTalkerMenu && loading)
        )
      );
    }),
    distinctUntilChanged()
  );

  /* *************************** Local Threads of Execution *************************** */

  /**
   * All messages coming from window get intercepted here. This means messages that the display app sends to the
   * dashboard app will be received here as well. Therefore, we must check if the specific message contains
   * relevant data, or else we can end up in an infinite loop.
   */
  private listenToMessages = fromEvent(window, 'message')
    .pipe(
      filter((event: MessageEvent) => {
        const origin = event?.origin?.toLowerCase();
        const hasData = exists(event?.data);
        return hasData && (origin?.includes('localhost') || origin?.includes('mybudsense.com'));
      }),
      map((event: MessageEvent) => event?.data)
    )
    .subscribeWhileAlive({
      owner: this,
      next: (data: any) => {
        if (data?.companyConfig) this.connectToCompanyConfig(data?.companyConfig);
        if (data?.locationConfig) this.connectToLocationConfig(data?.locationConfig);
        if (data?.printCardMenu) this.connectToPrintCardMenu(data?.printCardMenu);
        if (data?.variantIds) this.connectToVariantIds(data?.variantIds);
        if (data?.shelfTalkerMenu) this.connectToShelfTalkerMenu(data?.shelfTalkerMenu);
        if (data?.sectionId) this.connectToSectionId(data?.sectionId);
        if (data?.labels) this.connectToLabels(data?.labels);
        if (data?.metadata) this.connectToMetadata(data?.metadata);
      }
    });

  private sendCardDimensionsToDashboard = this.cardHeightAndWidth$.subscribeWhileAlive({
    owner: this,
    next: heightAndWidth => {
      window?.parent?.postMessage(heightAndWidth, '*');
    }
  });

  private sendLoadingAssetMetadataToDashboard = this.loadingVariantAssetsDashboardSignal$.subscribeWhileAlive({
    owner: this,
    next: loadingAssetMetadata => {
      window?.parent?.postMessage({ loadingAssetMetadata }, '*');
    }
  });

  private connectWithVariantAssetService = this.cardMenu$
    .pipe(filter(exists), distinctUntilChanged(DistinctUtils.distinctMenuById))
    .subscribeWhileAlive({
      owner: this,
      next: cardMenu => {
        this.variantAssetService.fetchVariantAssetsForMenusAssignedToDisplay([cardMenu]);
      }
    });
}
